import type { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import type { IEntity } from '@/plugins/cad/module/CADModule/model/entity'
import { MaterialUtils } from './MaterialUtils'

/** 实体使用的线型材质别名，统一指向 three LineMaterial */
export type LineTypeMaterial = LineMaterial

/** 材质颜色常量 */
export const MaterialColor = {
  hover: 0xffff00,
  select: 0x00ffff,
  locked: 0xa0a0a0,
}

type HighlightMaterialType = keyof typeof MaterialColor

/**
 * 材质缓存管理器，按线宽、线型、颜色组合共享材质，同时维护 hover/select/locked 高亮材质。
 */
export class MaterialManager {
  // 缓存的材质
  private cachedMaterialMap: Map<string, LineTypeMaterial> = new Map()
  // 已使用缓存材质的构件
  private usedCacheMaterialEntityMap: Map<string, Set<string>> = new Map()
  // hover/select/locked 高亮材质
  private cachedHighlightMaterialMap: Map<string, LineTypeMaterial> = new Map()

  private getCachedMaterialKey(entity: IEntity) {
    const { resolution: _resolution, ...options } = MaterialUtils.getMaterialOptionByEntity(entity)
    // resolution 是逐视口动态 uniform；其余创建参数均参与兼容性判定。
    return JSON.stringify([
      entity.lineType,
      Object.entries(options).sort(([a], [b]) => a.localeCompare(b)),
    ])
  }

  /**
   * 按实体样式获取缓存材质，同一线宽/线型/颜色组合会共享同一材质实例。
   *
   * @param entity - 目标实体
   * @returns 与实体样式匹配的缓存材质
   */
  getCachedMaterial(entity: IEntity) {
    const key = this.getCachedMaterialKey(entity)
    if (!this.cachedMaterialMap.has(key)) {
      const material = this.createMaterial(entity)
      material.userData = { ...material.userData, cacheKey: key }
      this.cachedMaterialMap.set(key, material)
    }
    if (!this.usedCacheMaterialEntityMap.has(key)) {
      this.usedCacheMaterialEntityMap.set(key, new Set())
    }
    this.usedCacheMaterialEntityMap.get(key)!.add(entity.id)
    return this.cachedMaterialMap.get(key)!
  }

  /**
   * 解绑指定实体对缓存材质的引用，当该材质不再被任何实体使用时释放。
   *
   * @param material - 目标缓存材质
   * @param entity - 不再使用该材质的实体
   */
  disposeMaterialByEntity(material: LineTypeMaterial, entity: IEntity) {
    const cacheKey = material.userData.cacheKey
    const usedSet = this.usedCacheMaterialEntityMap.get(cacheKey)
    usedSet?.delete(entity.id)
    if (usedSet && usedSet.size === 0) {
      this.cachedMaterialMap.delete(cacheKey)
      this.usedCacheMaterialEntityMap.delete(cacheKey)
      material.dispose()
    }
  }

  /**
   * 根据实体样式创建一个新的线型材质（不写入缓存）。
   *
   * @param entity - 目标实体
   * @returns 新建的线型材质
   */
  createMaterial(entity: IEntity) {
    const materialOption = MaterialUtils.getMaterialOptionByEntity(entity)
    return MaterialUtils.createMaterialByLineType(entity.lineType, materialOption)
  }

  updateLineTypeDashScale(dashScale: number) {
    MaterialUtils.setLineTypeDashScale(dashScale)
    this.cachedMaterialMap.forEach((material) =>
      MaterialUtils.updateLineTypeDashScale(material, dashScale)
    )
    this.cachedHighlightMaterialMap.forEach((material) =>
      MaterialUtils.updateLineTypeDashScale(material, dashScale)
    )
  }

  private getHighlightMaterialKey(type: HighlightMaterialType, entity: IEntity) {
    return `${type}_${MaterialUtils.getPixelWidth(entity.lineWidth)}_${entity.lineType}`
  }

  private getCachedHighlightMaterial(type: HighlightMaterialType, entity: IEntity) {
    const key = this.getHighlightMaterialKey(type, entity)
    if (!this.cachedHighlightMaterialMap.has(key)) {
      const materialOption = MaterialUtils.getMaterialOptionByEntity(entity)
      const material = MaterialUtils.createMaterialByLineType(entity.lineType, {
        ...materialOption,
        color: MaterialColor[type],
        linewidth: materialOption.linewidth + 2,
        depthTest: false,
        transparent: true,
        opacity: 0.5,
      })
      this.cachedHighlightMaterialMap.set(key, material)
    }
    return this.cachedHighlightMaterialMap.get(key)!
  }

  /**
   * 获取实体对应的预选高亮材质（黄色半透明）。
   *
   * @param entity - 目标实体
   * @returns 共享的 hover 材质
   */
  getCachedHoverMaterial(entity: IEntity) {
    return this.getCachedHighlightMaterial('hover', entity)
  }

  /**
   * 获取实体对应的选中高亮材质（青色半透明）。
   *
   * @param entity - 目标实体
   * @returns 共享的 select 材质
   */
  getCachedSelectMaterial(entity: IEntity) {
    return this.getCachedHighlightMaterial('select', entity)
  }

  /**
   * 获取锁定状态材质（用于编辑工具锁定选中态）。
   *
   * @param entity - 目标实体
   * @returns 共享的 locked 材质
   */
  getCachedLockedMaterial(entity: IEntity) {
    return this.getCachedHighlightMaterial('locked', entity)
  }

  /** 释放所有缓存材质并清空记录 */
  dispose() {
    this.cachedMaterialMap.forEach((material) => {
      material.dispose()
    })
    this.cachedHighlightMaterialMap.forEach((material) => {
      material.dispose()
    })
    this.cachedMaterialMap.clear()
    this.usedCacheMaterialEntityMap.clear()
    this.cachedHighlightMaterialMap.clear()
  }
}
